import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  User,
  UserPlus,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  Send,
  Camera,
  FileText,
  ExternalLink,
  History,
  Image,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/admin';
import { Button, Card, Badge, Modal, Input, Select, Spinner } from '@/components/common';
import { defects as defectsApi, projects as projectsApi, users as usersApi } from '@/utils/api';
import { useDefect } from '@/hooks/useDefects';
import { useUsers } from '@/hooks/useUsers';
import { useProjects } from '@/hooks/useProjects';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'info', next: ['scheduled', 'assigned'] },
  scheduled: { label: 'Terminiert', color: 'info', next: ['assigned'] },
  assigned: { label: 'Zugewiesen', color: 'warning', next: ['accepted', 'rejected'] },
  accepted: { label: 'Angenommen', color: 'warning', next: ['in_progress'] },
  in_progress: { label: 'In Bearbeitung', color: 'warning', next: ['completed', 'needs_followup'] },
  completed: { label: 'Erledigt', color: 'success', next: ['signature_refused', 'reported'] },
  signature_refused: { label: 'Unterschrift verweigert', color: 'danger', next: ['reported'] },
  needs_followup: { label: 'Nacharbeit', color: 'danger', next: ['assigned'] },
  reported: { label: 'Freigemeldet', color: 'success', next: [] },
};

const PRIORITY_CONFIG = {
  low: { label: 'Niedrig', color: 'success' },
  normal: { label: 'Normal', color: 'info' },
  high: { label: 'Hoch', color: 'warning' },
  urgent: { label: 'Dringend', color: 'danger' },
};

const MaengelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Server state
  const { data: defect, isLoading } = useDefect(id);
  const { data: allUsers } = useUsers({ role: 'all' });
  const { data: projects } = useProjects({ includeArchived: true });
  const workers = (allUsers ?? []).filter((u: any) => ['mitarbeiter', 'bauleiter', 'admin'].includes(u.role));

  // Modals
  const [editModal, setEditModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);

  // Form data
  const [editData, setEditData] = useState({
    property_address: '',
    tenant_name: '',
    tenant_phone: '',
    tenant_email: '',
    description: '',
    office_notes: '',
    category: '',
    priority: 'normal',
    deadline: '',
  });
  const [assignData, setAssignData] = useState({ assigned_user_id: '' });
  const [scheduleData, setScheduleData] = useState({ appointment_date: '', appointment_time: '', appointment_notes: '' });
  const [completeData, setCompleteData] = useState({ completion_notes: '' });
  const [reportData, setReportData] = useState({ reported_to: '' });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['defects', id] });

  const updateMutation = useMutation({
    mutationFn: (data: any) => defectsApi.update(id, data),
    onSuccess: () => { toast.success('Mängelmeldung aktualisiert'); setEditModal(false); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Aktualisieren'),
  });

  const assignMutation = useMutation({
    mutationFn: (data: any) => defectsApi.assign(id, data),
    onSuccess: () => { toast.success('Mitarbeiter zugewiesen - Benachrichtigung gesendet'); setAssignModal(false); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Zuweisen'),
  });

  const scheduleMutation = useMutation({
    mutationFn: (data: any) => defectsApi.schedule(id, data),
    onSuccess: () => { toast.success('Termin eingetragen'); setScheduleModal(false); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Terminieren'),
  });

  const noAppointmentMutation = useMutation({
    mutationFn: () => defectsApi.schedule(id, { appointment_date: null, appointment_notes: 'Kein Termin nötig' }),
    onSuccess: () => { toast.success('Als "Kein Termin nötig" markiert'); setScheduleModal(false); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Speichern'),
  });

  const startMutation = useMutation({
    mutationFn: () => defectsApi.start(id),
    onSuccess: () => { toast.success('Arbeit gestartet'); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Starten'),
  });

  const completeMutation = useMutation({
    mutationFn: (data: any) => defectsApi.complete(id, data),
    onSuccess: (response) => {
      if (response.data.signature_url) {
        toast.success(`Erledigt - Unterschrift-Link: ${response.data.signature_url}`);
      } else {
        toast.success('Als erledigt markiert');
      }
      setCompleteModal(false);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Abschließen'),
  });

  const reportMutation = useMutation({
    mutationFn: (data: any) => defectsApi.report(id, data),
    onSuccess: () => { toast.success('Mangel freigemeldet'); setReportModal(false); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler bei der Freimeldung'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => defectsApi.delete(id),
    onSuccess: () => { toast.success('Mängelmeldung gelöscht'); navigate('/admin/maengel'); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fehler beim Löschen'),
  });

  const skipSignatureMutation = useMutation({
    mutationFn: () => defectsApi.skipSignature(id),
    onSuccess: () => { toast.success('Als "Keine Unterschrift nötig" markiert'); invalidate(); },
    onError: () => toast.error('Fehler beim Aktualisieren'),
  });

  const handleUpdate = () => updateMutation.mutate(editData);

  const handleAssign = () => {
    if (!assignData.assigned_user_id) { toast.error('Bitte Mitarbeiter auswählen'); return; }
    assignMutation.mutate(assignData);
  };

  const handleSchedule = () => {
    if (!scheduleData.appointment_date) { toast.error('Bitte Datum angeben'); return; }
    const dateTime = scheduleData.appointment_time
      ? `${scheduleData.appointment_date}T${scheduleData.appointment_time}`
      : scheduleData.appointment_date;
    scheduleMutation.mutate({ appointment_date: dateTime, appointment_notes: scheduleData.appointment_notes });
  };

  const handleNoAppointmentNeeded = () => noAppointmentMutation.mutate();
  const handleStart = () => startMutation.mutate();
  const handleComplete = () => completeMutation.mutate(completeData);
  const handleReport = () => reportMutation.mutate(reportData);
  const handleDelete = () => deleteMutation.mutate();

  // Open edit modal and pre-fill form from current defect data
  const openEditModal = () => {
    if (defect) {
      setEditData({
        property_address: defect.property_address || '',
        tenant_name: defect.tenant_name || '',
        tenant_phone: defect.tenant_phone || '',
        tenant_email: defect.tenant_email || '',
        description: defect.description || '',
        office_notes: defect.office_notes || '',
        category: defect.category || '',
        priority: defect.priority || 'normal',
        deadline: defect.deadline || '',
      });
    }
    setEditModal(true);
  };

  const saving = updateMutation.isPending || assignMutation.isPending || scheduleMutation.isPending
    || noAppointmentMutation.isPending || startMutation.isPending || completeMutation.isPending
    || reportMutation.isPending || deleteMutation.isPending;

  const formatDate = (dateStr: any) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: any) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!defect) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Mängelmeldung nicht gefunden</p>
        <Link to="/admin/maengel" className="text-primary-600 hover:underline mt-2 inline-block">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[defect.status] || STATUS_CONFIG.new;
  const priorityConfig = PRIORITY_CONFIG[defect.priority] || PRIORITY_CONFIG.normal;
  const signatureUrl = defect.signature_token ?
    `${window.location.origin}/sign/${defect.signature_token}` : null;

  return (
    <div>
      <PageHeader
        title={`Mangel #${defect.id}`}
        description={defect.property_address || 'Keine Adresse'}
        actions={
          <>
            <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/admin/maengel')}>
              Zurück
            </Button>
            <Button variant="outline" icon={Edit} onClick={openEditModal}>
              Bearbeiten
            </Button>
            {user?.role === 'admin' && (
              <Button variant="danger" icon={Trash2} onClick={() => setDeleteModal(true)}>
                Löschen
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <Card.Title>Status</Card.Title>
                <Badge variant={statusConfig.color} size="lg">
                  {statusConfig.label}
                </Badge>
              </div>
            </Card.Header>
            <Card.Content>
              <div className="flex flex-wrap gap-2">
                {/* Schedule button */}
                {defect.status === 'new' && (
                  <Button variant="outline" icon={Calendar} onClick={() => setScheduleModal(true)}>
                    Termin festlegen
                  </Button>
                )}
                {defect.status === 'scheduled' && !defect.appointment_date && (
                  <Button variant="outline" icon={Calendar} disabled className="opacity-60">
                    ✓ Kein Termin nötig
                  </Button>
                )}
                {defect.status === 'scheduled' && defect.appointment_date && (
                  <Button variant="outline" icon={Calendar} onClick={() => setScheduleModal(true)}>
                    Termin ändern
                  </Button>
                )}

                {/* Assign button */}
                {['new', 'scheduled', 'needs_followup'].includes(defect.status) && (
                  <Button variant="primary" icon={UserPlus} onClick={() => setAssignModal(true)}>
                    Mitarbeiter zuweisen
                  </Button>
                )}

                {/* Start button */}
                {['accepted', 'assigned'].includes(defect.status) && (
                  <Button variant="primary" icon={Play} onClick={handleStart} loading={saving}>
                    Arbeit starten
                  </Button>
                )}

                {/* Complete button */}
                {defect.status === 'in_progress' && (
                  <Button variant="success" icon={CheckCircle} onClick={() => setCompleteModal(true)}>
                    Erledigt
                  </Button>
                )}

                {/* Report button */}
                {['completed', 'signature_refused'].includes(defect.status) && (
                  <Button variant="primary" icon={Send} onClick={() => setReportModal(true)}>
                    Freimelden
                  </Button>
                )}

                {/* Signature link + Skip button */}
                {defect.status === 'completed' && !defect.signature_path && (
                  <>
                    {signatureUrl && (
                      <Button
                        variant="outline"
                        icon={ExternalLink}
                        onClick={() => window.open(signatureUrl, '_blank')}
                      >
                        Unterschrift-Link
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => skipSignatureMutation.mutate()}
                      loading={skipSignatureMutation.isPending}
                    >
                      Keine Unterschrift nötig
                    </Button>
                  </>
                )}
              </div>

              {/* Assignment info */}
              {defect.assigned_user_id && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <User className="w-4 h-4 inline-block mr-1" />
                    Zugewiesen an: <strong>{defect.assigned_user_name || defect.assigned_user_id}</strong>
                  </p>
                  {defect.accepted_at && (
                    <p className="text-sm text-gray-500 mt-1">
                      Angenommen am: {formatDateTime(defect.accepted_at)}
                    </p>
                  )}
                </div>
              )}
            </Card.Content>
          </Card>

          {/* Description Card */}
          <Card>
            <Card.Header>
              <Card.Title>Mangelbeschreibung</Card.Title>
            </Card.Header>
            <Card.Content>
              <p className="whitespace-pre-wrap text-gray-700">
                {defect.description || 'Keine Beschreibung'}
              </p>

              {defect.situation_beschreibung && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-1">Aktuelle Situation:</p>
                  <p className="text-sm text-blue-700 whitespace-pre-wrap">{defect.situation_beschreibung}</p>
                </div>
              )}

              {defect.completion_notes && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800 mb-1">Erledigt-Notiz:</p>
                  <p className="text-sm text-green-700">{defect.completion_notes}</p>
                </div>
              )}

              {defect.office_notes && (
                <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-medium text-orange-800 mb-1">💼 Hinweise aus dem Büro:</p>
                  <p className="text-sm text-orange-700 whitespace-pre-wrap">{defect.office_notes}</p>
                </div>
              )}

              {defect.followup_reason && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Nacharbeit erforderlich:</p>
                  <p className="text-sm text-yellow-700">{defect.followup_reason}</p>
                </div>
              )}
            </Card.Content>
          </Card>

          {/* Original Documents Card */}
          {(defect.original_email || defect.original_pdf_path) && (
            <Card>
              <Card.Header>
                <Card.Title className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Originaldokumente
                </Card.Title>
              </Card.Header>
              <Card.Content>
                {defect.original_pdf_path && (
                  <a
                    href={defect.original_pdf_path.startsWith('/') ? defect.original_pdf_path : `/api/uploads/defects/${defect.original_pdf_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors mb-3"
                  >
                    <FileText className="w-5 h-5 text-red-500" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">Original-PDF</p>
                      <p className="text-xs text-gray-500">Mängelanzeige als PDF</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                )}
                {defect.original_email && (
                  <details className="group">
                    <summary className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                      <Mail className="w-5 h-5 text-blue-500" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">Original E-Mail</p>
                        <p className="text-xs text-gray-500">Klicken zum Anzeigen</p>
                      </div>
                      <span className="text-gray-400 text-xs group-open:hidden">&#x25BC;</span>
                      <span className="text-gray-400 text-xs hidden group-open:inline">&#x25B2;</span>
                    </summary>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                        {defect.original_email}
                      </pre>
                    </div>
                  </details>
                )}
              </Card.Content>
            </Card>
          )}

          {/* Photos Card */}
          {defect.photos && defect.photos.length > 0 && (
            <Card>
              <Card.Header>
                <Card.Title>Fotos</Card.Title>
              </Card.Header>
              <Card.Content>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {defect.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo.photo_path}
                        alt={`Foto ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90"
                        onClick={() => window.open(photo.photo_path, '_blank')}
                      />
                      <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
                        {photo.photo_type === 'before' ? 'Vorher' : photo.photo_type === 'after' ? 'Nachher' : 'Schaden'}
                      </span>
                    </div>
                  ))}
                </div>
              </Card.Content>
            </Card>
          )}

          {/* History Card */}
          {defect.history && defect.history.length > 0 && (
            <Card>
              <Card.Header>
                <Card.Title className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Verlauf
                </Card.Title>
              </Card.Header>
              <Card.Content>
                <div className="space-y-3">
                  {defect.history.map((entry, idx) => (
                    <div key={idx} className="flex gap-3 text-sm">
                      <div className="w-32 text-gray-500 flex-shrink-0">
                        {formatDateTime(entry.created_at)}
                      </div>
                      <div>
                        <span className="font-medium">{entry.user_name || 'System'}</span>
                        <span className="text-gray-600 ml-1">{entry.note}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Content>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info Card */}
          <Card>
            <Card.Header>
              <Card.Title>Details</Card.Title>
            </Card.Header>
            <Card.Content className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Projekt</p>
                <p className="font-medium">{defect.project_name || '-'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase">Kategorie</p>
                <p className="font-medium">{defect.category || '-'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase">Priorität</p>
                <Badge variant={priorityConfig.color}>{priorityConfig.label}</Badge>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase">Frist</p>
                <p className={`font-medium ${defect.deadline && new Date(defect.deadline) < new Date() ? 'text-red-600' : ''}`}>
                  {formatDate(defect.deadline)}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase">Termin</p>
                {defect.appointment_date ? (
                  <>
                    <p className="font-medium">{formatDateTime(defect.appointment_date)}</p>
                    {defect.appointment_notes && (
                      <p className="text-sm text-gray-500">{defect.appointment_notes}</p>
                    )}
                  </>
                ) : defect.appointment_notes === 'Kein Termin nötig' ? (
                  <p className="font-medium text-green-600">✓ Kein Termin nötig</p>
                ) : (
                  <p className="text-gray-400">Nicht festgelegt</p>
                )}
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase">Erstellt</p>
                <p className="text-sm">{formatDateTime(defect.created_at)}</p>
              </div>

              {defect.completed_at && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Erledigt</p>
                  <p className="text-sm">{formatDateTime(defect.completed_at)}</p>
                </div>
              )}

              {defect.reported_at && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Freigemeldet</p>
                  <p className="text-sm">{formatDateTime(defect.reported_at)}</p>
                  {defect.reported_to && (
                    <p className="text-sm text-gray-500">An: {defect.reported_to}</p>
                  )}
                </div>
              )}
            </Card.Content>
          </Card>

          {/* Location Card */}
          <Card>
            <Card.Header>
              <Card.Title>Objekt / Adresse</Card.Title>
            </Card.Header>
            <Card.Content className="space-y-3">
              {defect.gesellschaft && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Gesellschaft</p>
                  <p className="font-medium">{defect.gesellschaft}</p>
                </div>
              )}

              {(defect.adresse_strasse || defect.property_address) && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        defect.adresse_strasse
                          ? `${defect.adresse_strasse}, ${defect.adresse_plz || ''} ${defect.adresse_stadt || ''}`.trim()
                          : defect.property_address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline font-medium"
                    >
                      {defect.adresse_strasse
                        ? `${defect.adresse_strasse}, ${defect.adresse_plz || ''} ${defect.adresse_stadt || ''}`.trim()
                        : defect.property_address}
                    </a>
                    {defect.wohneinheit && (
                      <p className="text-sm text-gray-600">Einheit: {defect.wohneinheit}</p>
                    )}
                  </div>
                </div>
              )}

              {defect.reference_number && (
                <div>
                  <p className="text-xs text-gray-500 uppercase">Mangelnummer</p>
                  <p className="font-mono text-sm">{defect.reference_number}</p>
                </div>
              )}
            </Card.Content>
          </Card>

          {/* Tenant Card */}
          <Card>
            <Card.Header>
              <Card.Title>Mieter / Kontakt</Card.Title>
            </Card.Header>
            <Card.Content className="space-y-3">
              {defect.tenant_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{defect.tenant_name}</span>
                </div>
              )}

              {defect.tenant_phone && (
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    {defect.tenant_phone.split(/[,;]/).map((phone, idx) => (
                      <a
                        key={idx}
                        href={`tel:${phone.trim().replace(/\s/g, '')}`}
                        className="text-primary-600 hover:underline block"
                      >
                        {phone.trim()}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {defect.tenant_email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${defect.tenant_email}`} className="text-primary-600 hover:underline">
                    {defect.tenant_email}
                  </a>
                </div>
              )}

              {defect.weitere_kontakte && (
                <div>
                  <p className="text-xs text-gray-500 uppercase mt-3">Weitere Kontakte</p>
                  <p className="text-sm whitespace-pre-line">{defect.weitere_kontakte}</p>
                </div>
              )}

              {!defect.tenant_name && !defect.tenant_phone && !defect.tenant_email && (
                <p className="text-gray-500 text-sm">Keine Kontaktdaten hinterlegt</p>
              )}
            </Card.Content>
          </Card>

          {/* Signature Card */}
          {defect.signature_path && (
            <Card>
              <Card.Header>
                <Card.Title>Unterschrift</Card.Title>
              </Card.Header>
              <Card.Content>
                <img
                  src={`/api/uploads/defects/${defect.signature_path}`}
                  alt="Unterschrift"
                  className="w-full bg-gray-50 rounded-lg"
                />
              </Card.Content>
            </Card>
          )}

          {defect.signature_refused && (
            <Card className="border-red-200 bg-red-50">
              <Card.Content>
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Unterschrift verweigert</span>
                </div>
                {defect.signature_refused_reason && (
                  <p className="text-sm text-red-600 mt-2">{defect.signature_refused_reason}</p>
                )}
              </Card.Content>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal}
        onClose={() => setEditModal(false)}
        title="Mängelmeldung bearbeiten"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleUpdate} loading={saving}>
              Speichern
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Adresse / Objekt"
            value={editData.property_address}
            onChange={(e) => setEditData({ ...editData, property_address: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Mieter"
              value={editData.tenant_name}
              onChange={(e) => setEditData({ ...editData, tenant_name: e.target.value })}
            />
            <Input
              label="Telefon"
              value={editData.tenant_phone}
              onChange={(e) => setEditData({ ...editData, tenant_phone: e.target.value })}
            />
          </div>
          <Input
            label="E-Mail"
            type="email"
            value={editData.tenant_email}
            onChange={(e) => setEditData({ ...editData, tenant_email: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <textarea
              value={editData.description}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              💼 Hinweise aus dem Büro
              <span className="text-gray-400 font-normal ml-2">(für Mitarbeiter sichtbar)</span>
            </label>
            <textarea
              value={editData.office_notes}
              onChange={(e) => setEditData({ ...editData, office_notes: e.target.value })}
              rows={3}
              placeholder="Interne Hinweise für den Mitarbeiter..."
              className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-orange-50"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Kategorie"
              value={editData.category}
              onChange={(e) => setEditData({ ...editData, category: e.target.value })}
              options={[
                { value: '', label: 'Keine' },
                { value: 'Elektro', label: 'Elektro' },
                { value: 'Sanitaer', label: 'Sanitär' },
                { value: 'Heizung', label: 'Heizung' },
                { value: 'Sonstiges', label: 'Sonstiges' },
              ]}
            />
            <Select
              label="Priorität"
              value={editData.priority}
              onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
              options={Object.entries(PRIORITY_CONFIG).map(([value, { label }]) => ({ value, label }))}
            />
            <Input
              label="Frist"
              type="date"
              value={editData.deadline}
              onChange={(e) => setEditData({ ...editData, deadline: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* Assign Modal */}
      <Modal
        isOpen={assignModal}
        onClose={() => setAssignModal(false)}
        title="Mitarbeiter zuweisen"
        footer={
          <>
            <Button variant="outline" onClick={() => setAssignModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleAssign} loading={saving}>
              Zuweisen
            </Button>
          </>
        }
      >
        <p className="text-gray-600 mb-4">
          Der ausgewählte Mitarbeiter erhält eine Benachrichtigung.
        </p>
        <Select
          label="Mitarbeiter"
          value={assignData.assigned_user_id}
          onChange={(e) => setAssignData({ assigned_user_id: e.target.value })}
          options={[
            { value: '', label: 'Mitarbeiter wählen...' },
            ...workers.map(w => ({ value: w.id, label: w.name })),
          ]}
        />
      </Modal>

      {/* Schedule Modal */}
      <Modal
        isOpen={scheduleModal}
        onClose={() => setScheduleModal(false)}
        title="Termin festlegen"
        footer={
          <>
            <Button variant="outline" onClick={() => setScheduleModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleSchedule} loading={saving}>
              Speichern
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <button
            onClick={handleNoAppointmentNeeded}
            disabled={saving}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
          >
            Kein Termin nötig
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">oder Termin eingeben</span>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Datum"
                type="date"
                value={scheduleData.appointment_date}
                onChange={(e) => setScheduleData({ ...scheduleData, appointment_date: e.target.value })}
              />
            </div>
            <div className="w-32">
              <Input
                label="Uhrzeit"
                type="time"
                value={scheduleData.appointment_time}
                onChange={(e) => setScheduleData({ ...scheduleData, appointment_time: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notizen zum Termin</label>
            <textarea
              value={scheduleData.appointment_notes}
              onChange={(e) => setScheduleData({ ...scheduleData, appointment_notes: e.target.value })}
              rows={3}
              placeholder="z.B. Zugang über Hausmeister..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </Modal>

      {/* Complete Modal */}
      <Modal
        isOpen={completeModal}
        onClose={() => setCompleteModal(false)}
        title="Mangel als erledigt markieren"
        footer={
          <>
            <Button variant="outline" onClick={() => setCompleteModal(false)}>
              Abbrechen
            </Button>
            <Button variant="success" onClick={handleComplete} loading={saving}>
              Erledigt
            </Button>
          </>
        }
      >
        <p className="text-gray-600 mb-4">
          Nach der Markierung wird ein Link für die Mieter-Unterschrift generiert.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Erledigt-Notiz</label>
          <textarea
            value={completeData.completion_notes}
            onChange={(e) => setCompleteData({ completion_notes: e.target.value })}
            rows={3}
            placeholder="Was wurde gemacht?"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </Modal>

      {/* Report Modal */}
      <Modal
        isOpen={reportModal}
        onClose={() => setReportModal(false)}
        title="Mangel freimelden"
        footer={
          <>
            <Button variant="outline" onClick={() => setReportModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleReport} loading={saving}>
              Freimelden
            </Button>
          </>
        }
      >
        <p className="text-gray-600 mb-4">
          An wen soll die Freimeldung gesendet werden?
        </p>
        <Input
          label="E-Mail-Adresse (Hausverwaltung)"
          type="email"
          value={reportData.reported_to}
          onChange={(e) => setReportData({ reported_to: e.target.value })}
          placeholder="hausverwaltung@example.de"
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Mängelmeldung löschen"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteModal(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Löschen
            </Button>
          </>
        }
      >
        <p className="text-gray-600">
          Möchten Sie die Mängelmeldung #{defect.id} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
      </Modal>
    </div>
  );
};

export default MaengelDetail;
